import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, PrimaryButton } from "@/components/admin/ui";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";

const NUMS = [
  ["price_inr", "Assessment price (Rs.)", "Charged once per student assessment."],
  ["retake_cooldown_days", "Retake cooldown (days)", "Waiting period after a completed assessment before a new one can be bought."],
  ["straightline_threshold_pct", "Straight-lining threshold (%)", "Flag a session if the same option is chosen for more than this share of answers."],
];

const VERSIONS = ["assessment_version", "question_bank_version", "scoring_version", "career_mapping_version", "higher_ed_version", "report_template_version", "ai_prompt_version", "consent_current_version"];

export default function Settings() {
  const { data, setData } = useFetch("/admin/config");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setForm({ price_inr: data.price_inr, retake_cooldown_days: data.retake_cooldown_days, straightline_threshold_pct: data.straightline_threshold_pct, require_stronger_verification: data.require_stronger_verification }); }, [data]);
  if (!form) return <PageHeader title="Assessment settings" />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: updated } = await api.put("/admin/config", { ...form, price_inr: Number(form.price_inr), retake_cooldown_days: Number(form.retake_cooldown_days), straightline_threshold_pct: Number(form.straightline_threshold_pct) });
      setData(updated);
      toast.success("Settings saved");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Configuration" title="Assessment settings" description="Business rules applied to new purchases and sessions." />
      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 card-surface p-6 space-y-6">
          {NUMS.map(([k, label, hint]) => (
            <div key={k} className="space-y-2 max-w-sm">
              <Label className="font-semibold">{label}</Label>
              <Input type="number" required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-11" data-testid={`settings-${k}-input`} />
              <p className="text-xs text-[#635060]">{hint}</p>
            </div>
          ))}
          <label className="flex items-start justify-between gap-6 rounded-xl border border-[#E9DFE7] p-4">
            <span><span className="block text-sm font-semibold">Require stronger identity verification</span><span className="text-xs text-[#635060]">When on, e-signed consent also needs admin verification (e.g. ID or signed form review) before the assessment unlocks. Provider integration is configurable later.</span></span>
            <Switch checked={form.require_stronger_verification} onCheckedChange={(v) => setForm({ ...form, require_stronger_verification: v })} data-testid="settings-stronger-verification-switch" />
          </label>
          <PrimaryButton type="submit" busy={busy} data-testid="settings-save-button">Save settings</PrimaryButton>
        </form>
        <div className="card-surface p-6">
          <h2 className="font-display font-bold">Component versions</h2>
          <p className="text-xs text-[#635060] mt-1">Stamped on every report for immutability.</p>
          <ul className="mt-4 space-y-2">
            {VERSIONS.map((v) => <li key={v} className="flex justify-between text-sm"><span className="text-[#635060]">{v.replace(/_/g, " ")}</span><span className="font-mono font-semibold" data-testid={`version-${v}`}>v{data[v]}</span></li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}
